import { Component, OnInit } from '@angular/core';
import { NavigationContext, SessionContext } from '../../../../../core/services/config/app.settings';
import { UtilisateurTypeCode } from '../../../../../data/intervenant/models/enums/type-utilisateur.enum';
import { Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ReferentielService } from '../../../../../data/declaration/services/referentiel/referentiel.service';
import { NatureControle } from '../../../../../data/declaration/models/nature-controle.model';
import { Controle } from '../../../../../data/declaration/models/controle.model';
import { ControlesService } from '../../../../../data/declaration/services/controles/controles.service';
import { CahiersService } from '../../../../../data/commission/services/cahiers/cahiers.service';
import { Cahier } from '../../../../../data/habilitation/models/cahier.model';
import * as moment from 'moment';
import { of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { LoaderService } from '../../../../../core/services/loader/loader.service';

@Component({
    selector: 'app-operateur-controle',
    templateUrl: './operateur-controle.component.html',
    styleUrls: ['./operateur-controle.component.scss'],
    standalone: false
})
export class OperateurControleComponent implements OnInit {
    /**
     * Getter id operateur
     */
    private get idOperateur(): number {
        if (NavigationContext.has('idOperateur')) {
            return NavigationContext.get('idOperateur');
        } else {
            return SessionContext.get('idIntervenant');
        }
    }

    /**
     * Getter nature
     */
    get nature() {
        return this.formInitialGroup.get('nature');
    }

    /**
     * Getter année de controle
     */
    get anneeControle() {
        return this.formFilterGroup.get('anneeControle');
    }

    /**
     * Getter cahier des charges
     */
    get cdc() {
        return this.formFilterGroup.get('cdc');
    }

    constructor(
        private readonly router: Router,
        private readonly fb: UntypedFormBuilder,
        private readonly referentielService: ReferentielService,
        private readonly controlesService: ControlesService,
        private readonly loaderService: LoaderService,
        private readonly cahierService: CahiersService
    ) {
        this.estOperateur = SessionContext.get('utilisateurType') === UtilisateurTypeCode.OPERATEUR;
        this.idOrganisme = SessionContext.get('idOrganisme');
    }

    /**
     * Organisme id
     */
    idOrganisme: number;

    /**
     * True if operateur
     */
    estOperateur: boolean;

    /**
     * Initial form group
     */
    formInitialGroup: UntypedFormGroup;

    /**
     * Form filter group
     */
    formFilterGroup: UntypedFormGroup;

    /**
     * Referentiel nature
     */
    refNatures: NatureControle[];

    /**
     * List of controle
     */
    controles: Controle[];

    /**
     * Displayed controles
     */
    displayedControles: Controle[];

    /**
     * List of cahier associated to the id organisme
     */
    public cahiers: Cahier[];

    /**
     * Selected nature
     */
    selectedNature: NatureControle;

    /**
     * Dynamique columns for organoleptique and analytique natures
     */
    columns = [
        { field: 'dateControle', label: 'page.operateurs.controles.dateControle' },
        { field: 'produit', label: 'page.operateurs.controles.produit' },
        { field: 'millesime', label: 'page.operateurs.controles.millesime' },
        { field: 'logtLot', label: 'page.operateurs.controles.logtLot' },
        { field: 'volume', label: 'page.operateurs.controles.volume' },
        { field: 'typeMouvement', label: 'page.operateurs.controles.typeMouvement' },
        { field: 'expertisePassage', label: 'page.operateurs.controles.expertisePassage' },
        { field: 'resultat', label: 'page.operateurs.controles.resultat' }
    ];

    /**
     * On init component
     */
    ngOnInit() {
        if (!this.idOperateur) {
            this.router.navigate(['/main/operateurs/or/recherche-affilie']);
        }

        this.formInitialGroup = this.fb.group({
            nature: [undefined, Validators.required]
        });

        this.formFilterGroup = this.fb.group({
            anneeControle: [undefined, Validators.pattern(/(19|20)\d\d/)],
            cdc: undefined
        });

        this.referentielService.getReferentiel().subscribe(referentiel => {
            this.refNatures = referentiel.naturesControle;
        });

        this.cahierService.getCahierHabilitesByOperateur(this.idOperateur, this.idOrganisme).subscribe(cahierHabilite => {
            this.cahiers = cahierHabilite;
        });
    }

    /**
     *
     */
    onChangeNature() {
        this.selectedNature = this.refNatures.find(nature => nature.id === this.nature.value);
        this.controlesService
            .getControlesByIdOrganismeAndRefNature(this.idOperateur, this.nature.value, this.idOrganisme)
            .pipe(this.loaderService.operator())
            .subscribe(controles => {
                this.controles = controles.sort((a, b) => moment(b.dateControle).valueOf() - moment(a.dateControle).valueOf());
                this.displayedControles = [...this.controles];
            });
    }

    /**
     * On filter controle list
     */
    onFilter() {
        this.processFilter()
            .pipe(this.loaderService.operator())
            .subscribe(filteredControles => {
                this.displayedControles = filteredControles;
            });
    }

    /**
     * Process filter
     */
    processFilter() {
        return of(this.controles).pipe(
            delay(300),
            map(controles => {
                return controles.filter(controle => {
                    let result = true;
                    if (
                        this.anneeControle.value &&
                        this.anneeControle.valid &&
                        parseInt(this.anneeControle.value, 10) !== moment(controle.dateControle).year()
                    ) {
                        result = false;
                    }
                    if (this.cdc.value && this.cdc.value.length > 0) {
                        const codes = this.cdc.value.map(c => c.libelle.toLowerCase());
                        if (!codes.includes(controle.libelleCahier.toLowerCase())) {
                            result = false;
                        }
                    }
                    return result;
                });
            })
        );
    }
}
